/**
 * URL Builder Utilities
 *
 * This module provides utilities for building API request URLs with proper
 * path parameter substitution and query parameter encoding.
 *
 * Requirements: 5.4, 5.5, 13.5, 13.6
 */

/**
 * Substitutes path parameters into an endpoint path.
 *
 * Replaces placeholders in the format `{paramName}` with actual values.
 *
 * @param path - The endpoint path with parameter placeholders (e.g., "/users/{userId}/posts/{postId}")
 * @param pathParams - Object mapping parameter names to values
 * @returns The path with parameters substituted
 *
 * @example
 * substitutePathParams("/users/{userId}/posts/{postId}", { userId: "123", postId: "456" })
 * // Returns: "/users/123/posts/456"
 *
 * @example
 * substitutePathParams("/users/{userId}", {})
 * // Returns: "/users/{userId}" (no substitution if param not provided)
 *
 * Requirements: 5.4, 13.5
 */
export function substitutePathParams(path: string, pathParams?: Record<string, string>): string {
  if (!pathParams || Object.keys(pathParams).length === 0) {
    return path;
  }

  let result = path;

  // Replace each path parameter placeholder with its value
  for (const [paramName, paramValue] of Object.entries(pathParams)) {
    // Match {paramName} in the path
    const placeholder = `{${paramName}}`;
    result = result.replace(placeholder, encodeURIComponent(paramValue));
  }

  return result;
}

/**
 * Builds a URL-encoded query string from query parameters.
 *
 * Properly encodes parameter names and values according to URL encoding standards.
 * Returns an empty string if no parameters are provided.
 *
 * @param queryParams - Object mapping parameter names to values
 * @returns URL-encoded query string (without leading "?")
 *
 * @example
 * buildQueryString({ name: "John Doe", age: "30" })
 * // Returns: "name=John%20Doe&age=30"
 *
 * @example
 * buildQueryString({ search: "hello world", filter: "active" })
 * // Returns: "search=hello%20world&filter=active"
 *
 * @example
 * buildQueryString({})
 * // Returns: ""
 *
 * Requirements: 5.5, 13.6
 */
export function buildQueryString(queryParams?: Record<string, string>): string {
  if (!queryParams || Object.keys(queryParams).length === 0) {
    return '';
  }

  const params = new URLSearchParams();

  // Add each query parameter with proper encoding
  for (const [key, value] of Object.entries(queryParams)) {
    params.append(key, value);
  }

  return params.toString();
}

/**
 * Builds a complete request URL with base URL, path parameters, and query parameters.
 *
 * Combines all URL components with proper encoding and formatting.
 *
 * @param baseUrl - The base URL (e.g., "https://api.example.com")
 * @param path - The endpoint path with parameter placeholders
 * @param pathParams - Object mapping path parameter names to values
 * @param queryParams - Object mapping query parameter names to values
 * @returns Complete request URL
 *
 * @example
 * buildRequestUrl(
 *   "https://api.example.com",
 *   "/users/{userId}/posts",
 *   { userId: "123" },
 *   { limit: "10", offset: "0" }
 * )
 * // Returns: "https://api.example.com/users/123/posts?limit=10&offset=0"
 *
 * @example
 * buildRequestUrl("https://api.example.com", "/users", {}, {})
 * // Returns: "https://api.example.com/users"
 *
 * Requirements: 5.4, 5.5, 13.5, 13.6
 */
export function buildRequestUrl(
  baseUrl: string,
  path: string,
  pathParams?: Record<string, string>,
  queryParams?: Record<string, string>
): string {
  // Substitute path parameters
  const pathWithParams = substitutePathParams(path, pathParams);

  // Build query string
  const queryString = buildQueryString(queryParams);

  // Combine base URL and path (remove trailing slash from baseUrl if present)
  const normalizedBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
  let url = `${normalizedBaseUrl}${pathWithParams}`;

  // Append query string if present
  if (queryString) {
    url += `?${queryString}`;
  }

  return url;
}
